<script setup lang="ts">
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap } from 'maplibre-gl'
import { storeToRefs } from 'pinia'
import { watch, inject, ref, onUnmounted, type Ref, shallowRef } from 'vue'

defineOptions({ name: 'WorkspaceTourLayer' })

import { useTrackResolution, type Resolution } from '@/composables/useTrackResolution'
import { parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { TRACK_RESOLUTION_CACHE_INVALIDATED_EVENT } from '@/services/trackResolutionCacheService'
import { isTouchDevice } from '@/services/uiService'
import { useEventStore } from '@/stores/eventStore'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { EventType } from '@/types/event'
import type { Tour, Track, Waypoint } from '@/types/tour'
import { hideMapHoverPopup, showMapHoverPopup } from '@/utils/mapHoverPopup'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import { buildWorkspaceTrackHoverPopupHtml } from '@/utils/tourHoverPopup'
import { getBestTrackLineString } from '@/utils/trackGeometry'
import {
  buildTrackDirectionArrowLayer,
  buildTrackLineLayerIds,
  buildTrackLineLayers,
  buildTrackSurfaceDashLayer,
  TRACK_LINE_LAYOUT,
  TRACK_LINE_STYLE,
} from '@/utils/trackLineLayerFactory'
import {
  buildTrackProfileLegFeatureCollection,
  type TrackProfileAnalysis,
} from '@/utils/trackProfileAnalysis'
import { simplifyTrackPoints } from '@/utils/trackSimplification'

const props = defineProps<{
  tours: Tour[]
  hideTracks?: boolean
  /** Fade the tracks back (used to focus an active A→B sub-selection drawn on top at full opacity). */
  dimmed?: boolean
  suppressedTrack?: {
    tourId: string
    trackClientId: string
  } | null
  metricColorizedTrack?: {
    tourId: string
    trackClientId: string
    analysis: TrackProfileAnalysis
    baseColor?: string
    surfacePattern?: boolean
  } | null
}>()

const emit = defineEmits<{
  'track-click': [tourId: string, trackClientId: string]
  'track-overlap-select': [
    detail: {
      candidates: Array<{
        tourId: string
        trackClientId: string
        trackIndex: number
      }>
      clientX: number
      clientY: number
    },
  ]
  'track-context-menu': [
    detail: {
      tourId: string
      trackClientId: string
      trackIndex: number
      clientX: number
      clientY: number
    },
  ]
}>()

const uiStore = useUiStore()
const eventStore = useEventStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()
const workspaceStore = useWorkspaceStore()
const mapInstance = inject<Ref<MapLibreMap | null>>('mapInstance', ref(null))
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))
const { hoveredWaypointId, isTourHoverSuppressed, isTrackDrawingActive } = storeToRefs(uiStore)

const { targetResolution, getVisibleEntityIds, fetchTourGeometries, scheduleUpdate, clearCache } =
  useTrackResolution(mapInstance, mapLoaded)

const MAIN_SOURCE_ID = 'workspace-tour-main'
const OVERLAY_SOURCE_ID = 'workspace-tour-overlay'
const EDIT_TOUCH_SOURCE_ID = 'workspace-tour-edit-touch-source'
const EDIT_TOUCH_LAYER_ID = 'workspace-tour-edit-touch-layer'
const MAIN_LAYER_PREFIX = 'workspace-tour-main-'
const MAIN_IDS = buildTrackLineLayerIds('workspace-tour-main', 'layer')
const OVERLAY_IDS = buildTrackLineLayerIds('workspace-tour-overlay', 'layer')
const MAIN_SURFACE_DASH_LAYER_ID = 'workspace-tour-main-surface-dash-layer'
const OVERLAY_SURFACE_DASH_LAYER_ID = 'workspace-tour-overlay-surface-dash-layer'
const TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX = 12

const DEFAULT_OUTLINE_WIDTH = TRACK_LINE_STYLE.outlineWidth
const DEFAULT_LINE_WIDTH = TRACK_LINE_STYLE.lineWidth
const HOVER_OUTLINE_WIDTH = 12
const HOVER_LINE_WIDTH = 6
const HOVER_OUTLINE_OPACITY = 0.9

// Gently faded opacities applied to the tracks while an A→B sub-selection is the focus (the
// selection itself is redrawn at full opacity on top by TrackSegmentSelectionLayer). Kept subtle so
// the rest of the route — and its paved/unpaved surface dashes — stay readable.
const DIM_LINE_OPACITY = 0.55
const DIM_OUTLINE_OPACITY = 0.4
const DIM_ARROW_OPACITY = 0.4

// Fade the base/overlay tracks back (or restore them) by toggling layer opacities. Restores to the
// real defaults so a later un-dim doesn't leave the tracks stuck faded.
const applyTrackDim = (dim: boolean) => {
  const map = mapInstance.value
  if (!map) return
  const setOpacity = (
    layerId: string,
    property: 'line-opacity' | 'icon-opacity',
    value: number,
  ) => {
    if (map.getLayer(layerId)) map.setPaintProperty(layerId, property, value)
  }
  try {
    setOpacity(MAIN_IDS.lineId, 'line-opacity', dim ? DIM_LINE_OPACITY : 1)
    setOpacity(
      MAIN_IDS.outlineId,
      'line-opacity',
      dim ? DIM_OUTLINE_OPACITY : TRACK_LINE_STYLE.outlineOpacity,
    )
    setOpacity(
      MAIN_IDS.arrowId,
      'icon-opacity',
      dim ? DIM_ARROW_OPACITY : TRACK_LINE_STYLE.arrowOpacity,
    )
    setOpacity(OVERLAY_IDS.lineId, 'line-opacity', dim ? DIM_LINE_OPACITY : 1)
    setOpacity(
      OVERLAY_IDS.outlineId,
      'line-opacity',
      dim ? DIM_OUTLINE_OPACITY : HOVER_OUTLINE_OPACITY,
    )
    setOpacity(
      OVERLAY_IDS.arrowId,
      'icon-opacity',
      dim ? DIM_ARROW_OPACITY : TRACK_LINE_STYLE.arrowOpacity,
    )
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to apply track dim for segment selection', {
      error,
    })
  }
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
const isTouchEnvironment = isTouchDevice()

type OverrideResolution = Exclude<Resolution, '500m'>
type TrackCoordsById = Map<string, [number, number][]>
type LayerHandlers = {
  click: (e: maplibregl.MapLayerMouseEvent) => void
  contextmenu: (e: maplibregl.MapLayerMouseEvent) => void
  mouseenter: (e: maplibregl.MapLayerMouseEvent) => void
  mouseleave: () => void
  mousemove: (e: maplibregl.MapLayerMouseEvent) => void
  touchstart: (e: maplibregl.MapLayerTouchEvent) => void
  touchend: () => void
}

const hoverPopup = shallowRef<maplibregl.Popup | null>(null)
const renderedResolution = new Map<string, Resolution>()
const renderedGeometryCache = new Map<
  string,
  Partial<Record<OverrideResolution, TrackCoordsById>>
>()
const mainFeatureCache = new Map<string, GeoJSON.Feature[]>()
const registeredLayerHandlers = new Map<string, LayerHandlers>()
let layersInitialized = false
let forceHideStartResolution: Resolution | null = null
let longPressTimer: ReturnType<typeof setTimeout> | null = null
let longPressActivated = false
let longPressStartPoint: { x: number; y: number } | null = null

const cancelLongPress = () => {
  if (longPressTimer !== null) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
  longPressStartPoint = null
}

const isWithinLongPressMoveTolerance = (point: { x: number; y: number } | null) => {
  if (!longPressStartPoint || !point) return true
  const dx = point.x - longPressStartPoint.x
  const dy = point.y - longPressStartPoint.y
  return (
    dx * dx + dy * dy <= TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX * TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX
  )
}

const touchMoveLongPressHandler = (event: maplibregl.MapTouchEvent) => {
  if (isWithinLongPressMoveTolerance(event.point)) return
  cancelLongPress()
}

const resolveTrackId = (tourId: string, track: Track, index: number) =>
  track.clientId || `idx-${tourId}-${index}`

const hasUnsyncedLocalGeometryChanges = (tour: Tour) => {
  const trackIds = new Set(tour.tracks.map((track) => track.clientId).filter(Boolean))

  return eventStore.unsyncedEvents.some((event) => {
    switch (event.type) {
      case EventType.TOUR_CREATED:
      case EventType.TOUR_UPDATED:
      case EventType.TOUR_DELETED:
        return (event.payload as { clientId?: string }).clientId === tour.clientId
      case EventType.TRACK_CREATED:
        return (event.payload as { tour_client_id?: string }).tour_client_id === tour.clientId
      case EventType.TRACK_UPDATED:
      case EventType.TRACK_DELETED:
      case EventType.SEGMENT_CREATED:
      case EventType.SEGMENT_UPDATED:
      case EventType.SEGMENT_DELETED:
      case EventType.ROUTE_POINT_CREATED:
      case EventType.ROUTE_POINT_UPDATED:
      case EventType.ROUTE_POINT_DELETED: {
        const payload = event.payload as {
          clientId?: string
          trackClientId?: string
          track_client_id?: string
        }
        const trackId = payload.trackClientId ?? payload.track_client_id ?? payload.clientId
        return typeof trackId === 'string' && trackIds.has(trackId)
      }
      default:
        return false
    }
  })
}

const shouldPreferLocalGeometry = (tour: Tour) =>
  hasUnsyncedLocalGeometryChanges(tour) ||
  (uiStore.isTrackEditMode && uiStore.editingTrackTourId === tour.clientId) ||
  props.metricColorizedTrack?.tourId === tour.clientId

const isMetricColorizedTrack = (tourId: string, trackId: string) =>
  props.metricColorizedTrack?.tourId === tourId &&
  props.metricColorizedTrack?.trackClientId === trackId

const isSuppressedTrack = (tourId: string, trackId: string) =>
  props.suppressedTrack?.tourId === tourId && props.suppressedTrack?.trackClientId === trackId

const resolveSurfacePavedStatus = (categoryId: unknown) =>
  categoryId === 'paved' ? 'paved' : 'unpaved'

const discardCachedGeometry = (tourId: string) => {
  renderedGeometryCache.delete(tourId)
  renderedResolution.delete(tourId)
}

const parseTrackLineString = (lineString: string): [number, number][] =>
  parseLineString(lineString).map(
    (coordinate) => [coordinate[0], coordinate[1]] as [number, number],
  )

const resolveLocalTrackCoordinates = (track: Track): [number, number][] => {
  const lineString = getBestTrackLineString(track)
  if (lineString) return parseTrackLineString(lineString)

  return track.segments.flatMap((segment) =>
    segment.points
      .filter((point) => typeof point.lon === 'number' && typeof point.lat === 'number')
      .map((point: Waypoint) => [point.lon, point.lat] as [number, number]),
  )
}

const simplifyTrackCoordinates = (
  coordinates: [number, number][],
  toleranceM: number,
): [number, number][] =>
  simplifyTrackPoints(
    coordinates.map(([lon, lat]) => ({ lon, lat })),
    toleranceM,
  ).map(({ lon, lat }) => [lon, lat])

const resolveTrackBaseCoordinates = (tour: Tour, track: Track): [number, number][] => {
  if (shouldPreferLocalGeometry(tour)) {
    return resolveLocalTrackCoordinates(track)
  }

  if (targetResolution.value === '500m' && track.pointsLineReduced500m) {
    return parseTrackLineString(track.pointsLineReduced500m)
  }

  // A route-mode cache reset must not flash 500m geometry while the current resolution reloads.
  const localCoordinates = resolveLocalTrackCoordinates(track)
  return targetResolution.value === '50m'
    ? simplifyTrackCoordinates(localCoordinates, 50)
    : localCoordinates
}

const areTourTracksHidden = () => props.hideTracks || uiStore.areMapElementsForceHidden

const isTrackRenderable = (tour: Tour, track: Track) => {
  const isTrackVisible = track.visible !== undefined ? track.visible : tour.visible
  return isTrackVisible && !uiStore.isInWorkspacesOverview
}

const buildTrackFeatures = (
  tour: Tour,
  track: Track,
  trackIndex: number,
  overrideCoords?: [number, number][],
  options: { ignoreSuppression?: boolean } = {},
): GeoJSON.Feature[] => {
  const resolvedTrackId = resolveTrackId(tour.clientId, track, trackIndex)
  if (!options.ignoreSuppression && isSuppressedTrack(tour.clientId, resolvedTrackId)) return []
  if (!isTrackRenderable(tour, track)) return []

  if (isMetricColorizedTrack(tour.clientId, resolvedTrackId)) {
    const metricTrack = props.metricColorizedTrack
    const isSurfacePattern =
      metricTrack?.surfacePattern === true && metricTrack.analysis.metricId === 'surface'
    const surfacePatternColor =
      metricTrack?.baseColor ?? track.color ?? resolveTourAccentColor(tour)
    const metricFeatures = buildTrackProfileLegFeatureCollection(metricTrack?.analysis.legs ?? [])
    return metricFeatures.features.map((feature) => ({
      type: 'Feature' as const,
      properties: {
        ...feature.properties,
        tourId: tour.clientId,
        trackClientId: resolvedTrackId,
        trackIndex,
        tourName: tour.name,
        trackName: track.name || `Track ${trackIndex + 1}`,
        color: isSurfacePattern ? surfacePatternColor : feature.properties.color,
        surfacePavedStatus: isSurfacePattern
          ? resolveSurfacePavedStatus(feature.properties.categoryId)
          : null,
      },
      geometry: feature.geometry,
    }))
  }

  const coordinates = overrideCoords ?? resolveTrackBaseCoordinates(tour, track)
  if (coordinates.length < 2) return []
  const tourAccentColor = resolveTourAccentColor(tour)

  return [
    {
      type: 'Feature' as const,
      properties: {
        tourId: tour.clientId,
        trackClientId: resolvedTrackId,
        trackIndex,
        tourName: tour.name,
        trackName: track.name || `Track ${trackIndex + 1}`,
        color: tourAccentColor,
      },
      geometry: {
        type: 'LineString' as const,
        coordinates: coordinates as number[][],
      },
    },
  ]
}

const buildTourFeatures = (
  tour: Tour,
  overrideCoordsByTrack?: TrackCoordsById,
): GeoJSON.Feature[] =>
  tour.tracks.flatMap((track, trackIndex) =>
    buildTrackFeatures(
      tour,
      track,
      trackIndex,
      overrideCoordsByTrack?.get(resolveTrackId(tour.clientId, track, trackIndex)),
    ),
  )

const buildMainFC = (): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: areTourTracksHidden() ? [] : Array.from(mainFeatureCache.values()).flat(),
})

const buildEditTouchFC = (): GeoJSON.FeatureCollection => {
  if (
    !uiStore.isTrackEditMode ||
    !uiStore.editingTrackTourId ||
    !uiStore.editingTrackClientId ||
    uiStore.areMapElementsForceHidden
  ) {
    return EMPTY_FC
  }

  const tour = props.tours.find((candidate) => candidate.clientId === uiStore.editingTrackTourId)
  if (!tour) return EMPTY_FC

  return {
    type: 'FeatureCollection',
    features: tour.tracks.flatMap((track, trackIndex) =>
      resolveTrackId(tour.clientId, track, trackIndex) === uiStore.editingTrackClientId
        ? buildTrackFeatures(tour, track, trackIndex, undefined, { ignoreSuppression: true })
        : [],
    ),
  }
}

const buildOverlayFC = (): GeoJSON.FeatureCollection => {
  if (areTourTracksHidden()) return EMPTY_FC

  const isBatchSelectionSurface =
    mobileBatchSelectionStore.activeSurface === 'collection-detail' ||
    mobileBatchSelectionStore.activeSurface === 'pinned'
  const hoveredId = isBatchSelectionSurface ? null : workspaceStore.hoveredTourId
  const selectedId = isBatchSelectionSurface ? null : workspaceStore.selectedTourId
  const batchSelectedIds = isBatchSelectionSurface
    ? mobileBatchSelectionStore.tours
    : new Set<string>()
  const features: GeoJSON.Feature[] = []
  const renderedSelectedIds = new Set<string>()

  for (const tourId of batchSelectedIds) {
    renderedSelectedIds.add(tourId)
    features.push(
      ...(mainFeatureCache.get(tourId) ?? []).map((feature) => ({
        ...feature,
        properties: { ...feature.properties, role: 'selected' },
      })),
    )
  }

  if (selectedId && !renderedSelectedIds.has(selectedId)) {
    renderedSelectedIds.add(selectedId)
    features.push(
      ...(mainFeatureCache.get(selectedId) ?? []).map((feature) => ({
        ...feature,
        properties: { ...feature.properties, role: 'selected' },
      })),
    )
  }

  if (hoveredId && !renderedSelectedIds.has(hoveredId)) {
    features.push(
      ...(mainFeatureCache.get(hoveredId) ?? []).map((feature) => ({
        ...feature,
        properties: { ...feature.properties, role: 'hovered' },
      })),
    )
  }

  if (features.length === 0) return EMPTY_FC

  return {
    type: 'FeatureCollection',
    features,
  }
}

const syncMainCache = () => {
  const activeTourIds = new Set(props.tours.map((tour) => tour.clientId))
  for (const tourId of [...mainFeatureCache.keys()]) {
    if (!activeTourIds.has(tourId)) {
      mainFeatureCache.delete(tourId)
      discardCachedGeometry(tourId)
    }
  }

  for (const tour of props.tours) {
    if (shouldPreferLocalGeometry(tour)) {
      discardCachedGeometry(tour.clientId)
    }

    const currentResolution =
      targetResolution.value === '500m' ? '500m' : renderedResolution.get(tour.clientId)
    if (targetResolution.value === '500m' && renderedResolution.get(tour.clientId) !== '500m') {
      renderedResolution.set(tour.clientId, '500m')
    }
    const cachedCoordsByTrack =
      !shouldPreferLocalGeometry(tour) &&
      targetResolution.value !== '500m' &&
      currentResolution &&
      currentResolution !== '500m'
        ? renderedGeometryCache.get(tour.clientId)?.[currentResolution]
        : undefined

    mainFeatureCache.set(tour.clientId, buildTourFeatures(tour, cachedCoordsByTrack))
  }
}

const refreshMainSource = () => {
  const source = mapInstance.value?.getSource(MAIN_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(buildMainFC())
}

const refreshOverlaySource = () => {
  const source = mapInstance.value?.getSource(OVERLAY_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(buildOverlayFC())
}

const refreshEditTouchSource = () => {
  const source = mapInstance.value?.getSource(EDIT_TOUCH_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(buildEditTouchFC())
}

const buildEditTouchFilter = (): maplibregl.FilterSpecification => {
  if (!uiStore.isTrackEditMode || !uiStore.editingTrackTourId || !uiStore.editingTrackClientId) {
    return ['==', ['get', 'tourId'], '__no-edit-track__']
  }

  return [
    'all',
    ['==', ['get', 'tourId'], uiStore.editingTrackTourId],
    ['==', ['get', 'trackClientId'], uiStore.editingTrackClientId],
  ]
}

const applyEditTouchFilter = () => {
  const map = mapInstance.value
  if (!map?.getLayer(EDIT_TOUCH_LAYER_ID)) return
  map.setFilter(EDIT_TOUCH_LAYER_ID, buildEditTouchFilter())
}

const getFirstClusterLayerId = (map: MapLibreMap) => {
  const potentialClusterLayers = ['clusters-', 'photo-clusters']
  const layers = map.getStyle().layers || []

  for (const layer of layers) {
    for (const prefix of potentialClusterLayers) {
      if (layer.id && layer.id.startsWith(prefix)) {
        return layer.id
      }
    }
  }

  return undefined
}

const resolveTrackContext = (tourId: string, trackId: string, fallbackIndex: number) => {
  const tour = props.tours.find((candidate) => candidate.clientId === tourId)
  if (!tour) return null

  const indexedTrack = tour.tracks.findIndex(
    (candidate, index) => resolveTrackId(tourId, candidate, index) === trackId,
  )
  const trackIndex = indexedTrack >= 0 ? indexedTrack : fallbackIndex
  const track = tour.tracks[trackIndex]
  if (!track) return null

  return { tour, track, trackIndex }
}

// ── Tour hover popup ──────────────────────────────────────────────────────────
const hasActiveWaypointOrPOIHover = () => !!hoveredWaypointId.value

const showTourPopup = (
  lngLat: maplibregl.LngLatLike,
  tour: Tour,
  track: Track,
  trackIndex: number,
) => {
  const map = mapInstance.value
  if (!map) return

  const html = buildWorkspaceTrackHoverPopupHtml(tour, track, trackIndex)
  hoverPopup.value = showMapHoverPopup({
    map,
    popup: hoverPopup.value,
    lngLat,
    html,
  })
}

const hideTourPopup = () => {
  hoverPopup.value = hideMapHoverPopup(hoverPopup.value)
}

const showPopupForFeature = (
  event: maplibregl.MapLayerMouseEvent,
  tourId: string,
  trackId: string,
  trackIndex: number,
) => {
  if (isTouchEnvironment) return
  if (hasActiveWaypointOrPOIHover()) {
    hideTourPopup()
    return
  }
  if (isTourHoverSuppressed.value) {
    hideTourPopup()
    return
  }

  const context = resolveTrackContext(tourId, trackId, trackIndex)
  if (!context) return

  showTourPopup(event.lngLat, context.tour, context.track, context.trackIndex)
}

const getMarkerLayerIds = (map: MapLibreMap) =>
  (map.getStyle().layers || [])
    .filter((layer) => {
      const id = layer.id
      return (
        id === 'photo-clusters' ||
        id === 'photo-unclustered-point' ||
        id.startsWith('clusters-') ||
        id.startsWith('unclustered-point-') ||
        id.startsWith('cluster-count-')
      )
    })
    .map((layer) => layer.id)

const setupEventHandlers = (map: MapLibreMap) => {
  const getTrackFeaturesFromEvent = (
    event: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent,
  ) => {
    if (event.features?.length) return event.features
    if (!event.point || !map.getLayer(MAIN_IDS.touchTargetId)) return []
    return map.queryRenderedFeatures(event.point, { layers: [MAIN_IDS.touchTargetId] })
  }

  const getTrackFeatureFromEvent = (
    event: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent,
  ) => {
    return getTrackFeaturesFromEvent(event)[0] ?? null
  }

  const getClientPointFromEvent = (
    event: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent,
  ) => {
    const originalEvent = event.originalEvent as MouseEvent | TouchEvent | undefined
    if (originalEvent && 'clientX' in originalEvent && typeof originalEvent.clientX === 'number') {
      return { clientX: originalEvent.clientX, clientY: originalEvent.clientY }
    }
    if (originalEvent && 'changedTouches' in originalEvent) {
      const touch = originalEvent.changedTouches?.[0] ?? null
      if (touch) return { clientX: touch.clientX, clientY: touch.clientY }
    }
    const canvasRect = map.getCanvas().getBoundingClientRect?.()
    if (canvasRect && event.point) {
      return {
        clientX: canvasRect.left + event.point.x,
        clientY: canvasRect.top + event.point.y,
      }
    }
    return null
  }

  const getDistinctTrackCandidatesFromEvent = (
    event: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent,
  ) => {
    const seenTourIds = new Set<string>()
    const candidates: Array<{ tourId: string; trackClientId: string; trackIndex: number }> = []
    for (const feature of getTrackFeaturesFromEvent(event)) {
      const tourId = feature?.properties?.tourId as string | undefined
      const trackClientId = feature?.properties?.trackClientId as string | undefined
      const trackIndex = Number(feature?.properties?.trackIndex ?? 0)
      if (!tourId || !trackClientId || seenTourIds.has(tourId)) continue
      seenTourIds.add(tourId)
      candidates.push({ tourId, trackClientId, trackIndex })
    }
    return candidates
  }

  const applyHoverFromEvent = (event: maplibregl.MapLayerMouseEvent) => {
    if (hasActiveWaypointOrPOIHover()) {
      hideTourPopup()
      return
    }

    const feature = getTrackFeatureFromEvent(event)
    const tourId = feature?.properties?.tourId as string | undefined
    const trackId = feature?.properties?.trackClientId as string | undefined
    const trackIndex = Number(feature?.properties?.trackIndex ?? 0)
    if (!tourId || !trackId) return

    if (workspaceStore.activelyHoveredTourId !== tourId) {
      workspaceStore.setActivelyHoveredTourId(tourId)
    }
    if (workspaceStore.hoveredTourId !== tourId) {
      workspaceStore.setHoveredTourId(tourId)
    }

    showPopupForFeature(event, tourId, trackId, trackIndex)
  }

  const clickHandler = (event: maplibregl.MapLayerMouseEvent) => {
    if (isTrackDrawingActive.value) return
    if (uiStore.isTrackEditMode) return

    const markerLayerIds = getMarkerLayerIds(map)
    const markerFeatures =
      markerLayerIds.length > 0
        ? map.queryRenderedFeatures(event.point, { layers: markerLayerIds })
        : []
    if (markerFeatures.length > 0) return

    const candidates = getDistinctTrackCandidatesFromEvent(event)
    const feature = getTrackFeatureFromEvent(event)
    const tourId = feature?.properties?.tourId as string | undefined
    const trackId = feature?.properties?.trackClientId as string | undefined
    if (!tourId || !trackId) return

    if (
      mobileBatchSelectionStore.activeSurface === 'collection-detail' ||
      mobileBatchSelectionStore.activeSurface === 'pinned'
    ) {
      mobileBatchSelectionStore.toggle('tours', tourId)
      return
    }

    if (isTouchEnvironment) {
      if (longPressActivated) {
        longPressActivated = false
        return
      }
    }

    if (candidates.length > 1) {
      const clientPoint = getClientPointFromEvent(event)
      if (clientPoint) {
        emit('track-overlap-select', { candidates, ...clientPoint })
        return
      }
    }

    emit('track-click', tourId, trackId)
  }

  const openTrackContextMenu = (
    event: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent,
  ) => {
    if (isTrackDrawingActive.value || uiStore.isTrackEditMode) return

    const feature = getTrackFeatureFromEvent(event)
    const tourId = feature?.properties?.tourId as string | undefined
    const trackId = feature?.properties?.trackClientId as string | undefined
    const trackIndex = Number(feature?.properties?.trackIndex ?? 0)
    const originalEvent = event.originalEvent as MouseEvent | TouchEvent | undefined
    if (!tourId || !trackId || !originalEvent) return

    workspaceStore.setActivelyHoveredTourId(tourId)
    workspaceStore.setHoveredTourId(tourId)

    let clientX: number | null = null
    let clientY: number | null = null

    if ('clientX' in originalEvent && typeof originalEvent.clientX === 'number') {
      clientX = originalEvent.clientX
      clientY = originalEvent.clientY
    } else if ('changedTouches' in originalEvent) {
      const touch = originalEvent.changedTouches?.[0] ?? null
      clientX = touch?.clientX ?? null
      clientY = touch?.clientY ?? null
    }

    if (clientX === null || clientY === null) return

    emit('track-context-menu', { tourId, trackClientId: trackId, trackIndex, clientX, clientY })
  }

  const mouseEnterHandler = (event: maplibregl.MapLayerMouseEvent) => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = 'pointer'
    applyHoverFromEvent(event)
  }

  const mouseMoveHandler = (event: maplibregl.MapLayerMouseEvent) => {
    if (isTrackDrawingActive.value) return
    applyHoverFromEvent(event)
  }

  const mouseLeaveHandler = () => {
    if (isTrackDrawingActive.value) return
    map.getCanvas().style.cursor = ''
    if (hasActiveWaypointOrPOIHover()) {
      hideTourPopup()
      return
    }
    workspaceStore.setActivelyHoveredTourId(null)
    if (workspaceStore.selectedTourId) {
      workspaceStore.setHoveredTourId(workspaceStore.selectedTourId)
    } else if (workspaceStore.hoveredTourId !== null) {
      workspaceStore.setHoveredTourId(null)
    }
    if (!isTouchEnvironment) hideTourPopup()
  }

  const touchStartHandler = (event: maplibregl.MapLayerTouchEvent) => {
    if (isTrackDrawingActive.value || uiStore.isTrackEditMode) return
    const feature = getTrackFeatureFromEvent(event)
    const tourId = feature?.properties?.tourId as string | undefined
    const trackId = feature?.properties?.trackClientId as string | undefined
    if (!tourId || !trackId) return
    longPressActivated = false
    cancelLongPress()
    longPressStartPoint = event.point
    longPressTimer = setTimeout(() => {
      longPressTimer = null
      longPressActivated = true
      openTrackContextMenu(event)
    }, 500)
  }

  const touchEndHandler = () => {
    cancelLongPress()
  }

  const handlers: LayerHandlers = {
    click: clickHandler,
    contextmenu: openTrackContextMenu,
    mouseenter: mouseEnterHandler,
    mouseleave: mouseLeaveHandler,
    mousemove: mouseMoveHandler,
    touchstart: touchStartHandler,
    touchend: touchEndHandler,
  }

  const layerId = MAIN_IDS.touchTargetId
  registeredLayerHandlers.set(layerId, handlers)
  map.on('click', layerId, clickHandler)
  map.on('contextmenu', layerId, openTrackContextMenu)
  map.on('mouseenter', layerId, mouseEnterHandler)
  map.on('mouseleave', layerId, mouseLeaveHandler)
  map.on('mousemove', layerId, mouseMoveHandler)
  map.on('touchstart', layerId, touchStartHandler)
  map.on('touchend', layerId, touchEndHandler)
  map.on('touchmove', touchMoveLongPressHandler)
}

const removeEventHandlers = (map: MapLibreMap) => {
  map.off('touchmove', touchMoveLongPressHandler)
  for (const [layerId, handlers] of registeredLayerHandlers) {
    try {
      map.off('click', layerId, handlers.click)
      map.off('contextmenu', layerId, handlers.contextmenu)
      map.off('mouseenter', layerId, handlers.mouseenter)
      map.off('mouseleave', layerId, handlers.mouseleave)
      map.off('mousemove', layerId, handlers.mousemove)
      map.off('touchstart', layerId, handlers.touchstart)
      map.off('touchend', layerId, handlers.touchend)
    } catch {
      // The style can remove layers before the component gets its cleanup turn.
    }
  }
  registeredLayerHandlers.clear()
}

const initializeLayers = () => {
  const map = mapInstance.value
  if (!map) return

  const firstClusterLayer = getFirstClusterLayerId(map)

  map.addSource(MAIN_SOURCE_ID, {
    type: 'geojson',
    data: buildMainFC(),
  })
  map.addSource(EDIT_TOUCH_SOURCE_ID, {
    type: 'geojson',
    data: buildEditTouchFC(),
  })

  const mainLayers = buildTrackLineLayers({
    sourceId: MAIN_SOURCE_ID,
    ids: MAIN_IDS,
    lineColor: ['get', 'color'],
  })
  mainLayers.orderedLayers.forEach((layer) => {
    map.addLayer(layer, firstClusterLayer)
  })
  map.addLayer(
    buildTrackSurfaceDashLayer({
      id: MAIN_SURFACE_DASH_LAYER_ID,
      sourceId: MAIN_SOURCE_ID,
    }),
    MAIN_IDS.arrowId,
  )

  map.addLayer(
    {
      id: EDIT_TOUCH_LAYER_ID,
      type: 'line',
      source: EDIT_TOUCH_SOURCE_ID,
      filter: buildEditTouchFilter(),
      layout: { ...TRACK_LINE_LAYOUT },
      paint: {
        'line-color': 'transparent',
        'line-width': TRACK_LINE_STYLE.touchTargetWidth,
        'line-opacity': 0,
      },
    },
    firstClusterLayer,
  )

  map.addSource(OVERLAY_SOURCE_ID, {
    type: 'geojson',
    data: buildOverlayFC(),
  })

  map.addLayer(
    {
      id: OVERLAY_IDS.outlineId,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      layout: { ...TRACK_LINE_LAYOUT },
      paint: {
        'line-color': '#ffffff',
        'line-width': [
          'case',
          ['any', ['==', ['get', 'role'], 'hovered'], ['==', ['get', 'role'], 'selected']],
          HOVER_OUTLINE_WIDTH,
          DEFAULT_OUTLINE_WIDTH,
        ],
        'line-opacity': HOVER_OUTLINE_OPACITY,
      },
    },
    firstClusterLayer,
  )
  map.addLayer(
    {
      id: OVERLAY_IDS.lineId,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      layout: { ...TRACK_LINE_LAYOUT },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': [
          'case',
          ['any', ['==', ['get', 'role'], 'hovered'], ['==', ['get', 'role'], 'selected']],
          HOVER_LINE_WIDTH,
          DEFAULT_LINE_WIDTH,
        ],
      },
    },
    firstClusterLayer,
  )
  map.addLayer(
    buildTrackSurfaceDashLayer({
      id: OVERLAY_SURFACE_DASH_LAYER_ID,
      sourceId: OVERLAY_SOURCE_ID,
    }),
    firstClusterLayer,
  )
  map.addLayer(
    buildTrackDirectionArrowLayer({
      id: OVERLAY_IDS.arrowId,
      sourceId: OVERLAY_SOURCE_ID,
    }),
    firstClusterLayer,
  )

  setupEventHandlers(map)
  layersInitialized = true
  // Re-apply the current dim state after a (re)build so it survives layer recreation.
  applyTrackDim(!!props.dimmed)
}

const removeLayers = () => {
  const map = mapInstance.value
  if (!map) return

  removeEventHandlers(map)

  const layerIds = [
    EDIT_TOUCH_LAYER_ID,
    MAIN_IDS.touchTargetId,
    MAIN_IDS.outlineId,
    MAIN_IDS.lineId,
    MAIN_SURFACE_DASH_LAYER_ID,
    MAIN_IDS.arrowId,
    OVERLAY_IDS.outlineId,
    OVERLAY_IDS.lineId,
    OVERLAY_SURFACE_DASH_LAYER_ID,
    OVERLAY_IDS.arrowId,
  ]

  for (const layerId of layerIds) {
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Error removing workspace tour layer', {
        layerId,
        error,
      })
    }
  }

  for (const sourceId of [MAIN_SOURCE_ID, OVERLAY_SOURCE_ID, EDIT_TOUCH_SOURCE_ID]) {
    try {
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Error removing workspace tour source', {
        sourceId,
        error,
      })
    }
  }

  layersInitialized = false
}

const cleanupLayers = () => {
  removeLayers()
  mainFeatureCache.clear()
  hideTourPopup()
}

const updateLayers = (forceRecreate = false) => {
  if (!mapInstance.value || !mapLoaded.value) return

  if (forceRecreate) {
    removeLayers()
  }

  syncMainCache()

  if (!layersInitialized) {
    initializeLayers()
  } else {
    refreshMainSource()
    refreshOverlaySource()
    refreshEditTouchSource()
    applyEditTouchFilter()
  }

  const hasVisibleTrack =
    !areTourTracksHidden() &&
    props.tours.some((tour) => tour.tracks.some((track) => isTrackRenderable(tour, track)))
  if (!hasVisibleTrack) {
    hideTourPopup()
  }
}

const buildOverrideCoordsByTrack = (tour: Tour, coords: [number, number][][]): TrackCoordsById => {
  const byTrack = new Map<string, [number, number][]>()

  coords.forEach((trackCoords, trackIndex) => {
    if (trackCoords.length < 2) return
    const track = tour.tracks[trackIndex]
    if (!track) return
    byTrack.set(resolveTrackId(tour.clientId, track, trackIndex), trackCoords)
  })

  return byTrack
}

const updateTourSourceResolution = async (signal: AbortSignal) => {
  const map = mapInstance.value
  const resolution = targetResolution.value
  if (!map || !layersInitialized || areTourTracksHidden()) return

  if (resolution === '500m') {
    let changed = false
    for (const tour of props.tours) {
      if (renderedResolution.get(tour.clientId) === '500m') continue
      mainFeatureCache.set(tour.clientId, buildTourFeatures(tour))
      renderedResolution.set(tour.clientId, '500m')
      changed = true
    }
    if (changed) {
      refreshMainSource()
      refreshOverlaySource()
      refreshEditTouchSource()
    }
    return
  }

  const visibleTourIds = Array.from(
    new Set(getVisibleEntityIds(MAIN_LAYER_PREFIX, 'tourId')),
  ).filter((tourId) => {
    const tour = props.tours.find((candidate) => candidate.clientId === tourId)
    if (!tour) return false
    return !shouldPreferLocalGeometry(tour)
  })
  if (visibleTourIds.length === 0) return

  const visibleTourIdSet = new Set(visibleTourIds)
  let changed = false

  for (const tour of props.tours) {
    if (visibleTourIdSet.has(tour.clientId)) continue
    if (shouldPreferLocalGeometry(tour)) continue
    if (renderedResolution.get(tour.clientId) === '500m') continue

    mainFeatureCache.set(tour.clientId, buildTourFeatures(tour))
    renderedResolution.set(tour.clientId, '500m')
    changed = true
  }

  const geometries = await fetchTourGeometries(visibleTourIds, resolution, signal)

  for (const [tourId, coords] of geometries) {
    // Skip if already rendered at this resolution — avoids a no-op source swap
    // when panning/zooming over tours whose geometry hasn't changed.
    if (renderedResolution.get(tourId) === resolution) continue

    const tour = props.tours.find((candidate) => candidate.clientId === tourId)
    if (!tour) continue

    const overrideCoordsByTrack = buildOverrideCoordsByTrack(tour, coords)
    const cachedResolutions = renderedGeometryCache.get(tourId) ?? {}
    cachedResolutions[resolution] = overrideCoordsByTrack
    renderedGeometryCache.set(tourId, cachedResolutions)

    mainFeatureCache.set(tourId, buildTourFeatures(tour, overrideCoordsByTrack))
    renderedResolution.set(tourId, resolution)
    changed = true
  }

  if (changed) {
    refreshMainSource()
    refreshOverlaySource()
    refreshEditTouchSource()
  }
}

const handleMoveEnd = () => {
  if (!areTourTracksHidden() && targetResolution.value !== '500m') {
    scheduleUpdate(updateTourSourceResolution)
  }
}

const resetResolutionCaches = () => {
  clearCache()
  renderedGeometryCache.clear()
  renderedResolution.clear()
}

const handleTrackResolutionCacheInvalidated = () => {
  resetResolutionCaches()
  if (!mapLoaded.value) return

  updateLayers()
  if (targetResolution.value !== '500m' && !areTourTracksHidden()) {
    queueMicrotask(() => {
      scheduleUpdate(updateTourSourceResolution)
    })
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener(
    TRACK_RESOLUTION_CACHE_INVALIDATED_EVENT,
    handleTrackResolutionCacheInvalidated,
  )
}

const scheduleResolutionUpdateAfterRestoredSource = () => {
  const map = mapInstance.value
  if (!map) return

  map.once('idle', () => {
    if (!areTourTracksHidden()) {
      scheduleUpdate(updateTourSourceResolution)
    }
  })
}

watch(targetResolution, () => {
  if (areTourTracksHidden()) return
  scheduleUpdate(updateTourSourceResolution)
})

watch(
  [mapInstance, mapLoaded],
  ([map, loaded], previousValues) => {
    const previousMap = previousValues?.[0] ?? null
    previousMap?.off('moveend', handleMoveEnd)
    if (map && loaded) {
      map.on('moveend', handleMoveEnd)
    }
  },
  { immediate: true },
)

watch(
  () => props.dimmed,
  (dim) => applyTrackDim(!!dim),
)

watch(
  [
    mapLoaded,
    () => props.tours,
    () => uiStore.isInWorkspacesOverview,
    () => uiStore.isTrackEditMode,
    () => uiStore.editingTrackTourId,
    () => uiStore.editingTrackClientId,
    () => props.hideTracks,
    () => props.suppressedTrack,
  ],
  ([loaded]) => {
    if (!loaded) return

    updateLayers()
    if (targetResolution.value !== '500m' && !areTourTracksHidden()) {
      queueMicrotask(() => {
        scheduleUpdate(updateTourSourceResolution)
      })
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => props.metricColorizedTrack,
  () => {
    if (!mapLoaded.value) return
    updateLayers()
  },
  { deep: true },
)

watch(isTourHoverSuppressed, (shouldHide) => {
  if (shouldHide) {
    hideTourPopup()
  }
})

watch(
  () => uiStore.areMapElementsForceHidden,
  (isHidden) => {
    if (!mapLoaded.value) return
    if (isHidden) {
      forceHideStartResolution = targetResolution.value
      updateLayers()
      return
    }

    const shouldRefreshResolution =
      forceHideStartResolution !== null && targetResolution.value !== forceHideStartResolution
    forceHideStartResolution = null

    updateLayers()
    if (shouldRefreshResolution && targetResolution.value !== '500m') {
      scheduleResolutionUpdateAfterRestoredSource()
    }
  },
)

watch(
  () => mapStyleRevision.value,
  () => {
    if (!mapLoaded.value) return

    updateLayers(true)
    if (targetResolution.value !== '500m' && !areTourTracksHidden()) {
      queueMicrotask(() => {
        scheduleUpdate(updateTourSourceResolution)
      })
    }
  },
)

watch(
  () => hoveredWaypointId.value,
  (nextHoveredWaypointId) => {
    if (nextHoveredWaypointId) {
      hideTourPopup()
    }
  },
)

watch(
  () => workspaceStore.hoveredTourId,
  (hoveredTourId) => {
    refreshOverlaySource()
    if (hoveredTourId === null) hideTourPopup()
  },
)

watch(
  () => workspaceStore.selectedTourId,
  () => {
    refreshOverlaySource()
  },
)

watch(
  () => [
    mobileBatchSelectionStore.activeSurface,
    [...mobileBatchSelectionStore.tours].sort().join('|'),
  ],
  () => {
    refreshOverlaySource()
  },
)

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener(
      TRACK_RESOLUTION_CACHE_INVALIDATED_EVENT,
      handleTrackResolutionCacheInvalidated,
    )
  }
  mapInstance.value?.off('moveend', handleMoveEnd)
  if (workspaceStore.hoveredTourId !== null) {
    workspaceStore.setHoveredTourId(null)
  }
  cleanupLayers()
})
</script>

<template>
  <div class="tour-layer"></div>
</template>
